import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';
import img1 from '../Assests/slide_1.webp';
import img2 from '../Assests/slide_2.webp';
import img3 from '../Assests/slide_3.webp';

const ImageSliderSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);

  const slides = [
    {
      id: 0,
      image: img1,
      title: "Robotics Club-IIT Delhi Display of Various Robots",
      bgColor: "bg-red-600",
      alt: "Robotics Display"
    },
    {
      id: 1,
      image: img2,
      title: "Nanoscale Research Facility (NRF) at IIT Delhi",
      bgColor: "bg-yellow-600",
      alt: "Research Facility"
    },
    {
      id: 2,
      image: img3,
      title: "Fun Activities like Glass Blowing and Glass Molding",
      bgColor: "bg-blue-600",
      alt: "Glass Activities"
    }
  ];

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlay) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlay, slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const toggleAutoPlay = () => {
    setIsAutoPlay(!isAutoPlay);
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Play className="w-4 h-4" />
            Event Highlights
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Experience IIT Delhi
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Discover the innovation, research, and exciting activities that make IIT Delhi a world-class institution
          </p>
        </div>

        {/* Slider Container */}
        <div className="relative w-full max-w-6xl mx-auto">
          {/* Main Slider */}
          <div className="relative w-full overflow-hidden rounded-2xl shadow-2xl bg-white">
            <div 
              className="flex transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {slides.map((slide, index) => (
                <div 
                  key={slide.id}
                  className="w-full flex-shrink-0 h-auto aspect-[1152/240] relative"
                >
                  {/* Colored Background Strip */}
                  <div className={`${slide.bgColor} w-1/5 h-full absolute z-10`}></div>
                  
                  {/* Content Container */}
                  <div className="h-full flex items-center absolute left-[2%] right-[2%] z-20">
                    {/* Image */}
                    <img
                      alt={slide.alt}
                      src={slide.image}
                      className="h-[80%] w-auto aspect-video object-cover rounded-lg shadow-lg"
                      onError={(e) => {
                        // Fallback if image fails to load
                        e.target.src = `https://via.placeholder.com/800x450/e5e7eb/6b7280?text=${encodeURIComponent(slide.title)}`;
                      }}
                    />
                    
                    {/* Title */}
                    <div className="flex-1 pl-8">
                      <p className="uppercase font-semibold text-gray-800 sm:text-lg md:text-xl lg:text-2xl leading-tight">
                        {slide.title}
                      </p>
                    </div>
                  </div>

                  {/* Gradient Overlay for better text readability */}
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent z-15"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 z-30 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-30 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Auto-play Control */}
          <button
            onClick={toggleAutoPlay}
            className="absolute top-4 right-4 z-30 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110"
            aria-label={isAutoPlay ? "Pause slideshow" : "Play slideshow"}
          >
            {isAutoPlay ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </button>
        </div>

        {/* Slide Indicators */}
        <div className="flex justify-center mt-8 gap-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                currentSlide === index
                  ? 'w-12 h-3 bg-blue-600'
                  : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Slide Counter */}
        <div className="text-center mt-4">
          <span className="text-gray-600 font-medium">
            {currentSlide + 1} of {slides.length}
          </span>
        </div>

     
      </div>
    </section>
  );
};

export default ImageSliderSection;